import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, Plus, Trash2, Copy, Percent, DollarSign, Calendar, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CouponManager() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase: 0,
    max_uses: '',
    expires_at: ''
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/coupons/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(res.data.coupons || []);
    } catch (error) {
      console.error('Failed to load coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCoupon.code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/coupons`,
        {
          ...newCoupon,
          code: newCoupon.code.toUpperCase(),
          max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
          expires_at: newCoupon.expires_at || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Coupon created!');
      setShowForm(false);
      setNewCoupon({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_purchase: 0,
        max_uses: '',
        expires_at: ''
      });
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCoupon = async (couponId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/coupons/${couponId}?is_active=${!currentStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadCoupons();
      toast.success(currentStatus ? 'Coupon deactivated' : 'Coupon activated');
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <Tag className="w-5 h-5 text-[#00F0FF]" />
          Coupon Codes
        </h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Create Coupon Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-neutral-800/50 rounded-xl p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-2">Coupon Code</label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SAVE20"
                className="bg-neutral-900 border-white/10 uppercase"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">Discount Type</label>
              <select
                value={newCoupon.discount_type}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                className="w-full px-4 py-2 bg-neutral-900 border border-white/10 rounded-lg"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                Discount Value {newCoupon.discount_type === 'percentage' ? '(%)' : '($)'}
              </label>
              <Input
                type="number"
                value={newCoupon.discount_value}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: parseFloat(e.target.value) || 0 })}
                className="bg-neutral-900 border-white/10"
                min="0"
                max={newCoupon.discount_type === 'percentage' ? 100 : undefined}
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">Min. Purchase ($)</label>
              <Input
                type="number"
                value={newCoupon.min_purchase}
                onChange={(e) => setNewCoupon({ ...newCoupon, min_purchase: parseFloat(e.target.value) || 0 })}
                className="bg-neutral-900 border-white/10"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">Max Uses (optional)</label>
              <Input
                type="number"
                value={newCoupon.max_uses}
                onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })}
                placeholder="Unlimited"
                className="bg-neutral-900 border-white/10"
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-2">Expiration Date (optional)</label>
            <Input
              type="datetime-local"
              value={newCoupon.expires_at}
              onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
              className="bg-neutral-900 border-white/10 max-w-xs"
            />
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={submitting} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
              {submitting ? 'Creating...' : 'Create Coupon'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Coupons List */}
      {coupons.length === 0 ? (
        <div className="text-center py-12 bg-neutral-900/50 rounded-2xl border border-white/10">
          <Tag className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No coupons yet</h3>
          <p className="text-neutral-400">Create your first coupon to offer discounts to your customers.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {coupons.map((coupon) => (
            <div
              key={coupon.coupon_id}
              className={`p-4 bg-neutral-900/50 rounded-xl border ${
                coupon.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="px-4 py-2 bg-[#00F0FF]/10 rounded-lg font-mono font-bold text-[#00F0FF] cursor-pointer hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-2"
                    onClick={() => copyCode(coupon.code)}
                  >
                    {coupon.code}
                    <Copy className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    {coupon.discount_type === 'percentage' ? (
                      <>
                        <Percent className="w-4 h-4 text-[#00F0FF]" />
                        <span className="font-bold">{coupon.discount_value}% off</span>
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-4 h-4 text-[#00F0FF]" />
                        <span className="font-bold">${coupon.discount_value} off</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-sm text-neutral-400">
                    {coupon.current_uses} / {coupon.max_uses || '∞'} uses
                  </div>
                  <button
                    onClick={() => toggleCoupon(coupon.coupon_id, coupon.is_active)}
                    className="text-neutral-400 hover:text-white"
                  >
                    {coupon.is_active ? (
                      <ToggleRight className="w-6 h-6 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-6 h-6" />
                    )}
                  </button>
                  <button
                    onClick={() => deleteCoupon(coupon.coupon_id)}
                    className="text-neutral-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mt-3 text-xs text-neutral-500">
                {coupon.min_purchase > 0 && (
                  <span>Min. ${coupon.min_purchase}</span>
                )}
                {coupon.expires_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Expires: {new Date(coupon.expires_at).toLocaleDateString()}
                  </span>
                )}
                {!coupon.is_active && (
                  <span className="text-red-400">Inactive</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CouponManager;
